import {isFeatureEnabled} from 'feature-flags';
import {Action, Actions, Card, Separator} from 'platform/components';
import {Box, Center, getSize, Hide, HStack, Show, Text, VStack} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';

import {useRef} from 'react';

import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {suffixTestId, TestIdProps} from 'shared/web';

import {useIsSourcingCardViewEnforced} from '../../../../../hooks/useIsSourcingCardViewEnforced';
import {useSourcingPageHeaderHeight} from '../../../../../hooks/useSourcingPageHeaderHeight';
import {useSourcingType} from '../../../../../hooks/useSourcingType';
import {SourcingFilterPresetsControl} from '../../../hooks/useSourcingFilterPresets';
import {getApplicableFilters} from '../../../utils/getApplicableFilters';
import {hasSomeFilters} from '../../../utils/hasSomeFilters';
import {isSuspiciousReasonFilterEnabled} from '../../../utils/isSuspiciousReasonFilterEnabled';
import {SavedFiltersMobileBar} from '../../FilterPresetButtons/components/SavedFiltersMobileBar';
import {FilterPresetButtons} from '../../FilterPresetButtons/FilterPresetButtons';
import {useFilters} from '../../FiltersContext/FiltersContext';
import {FilterTags} from '../../FilterTags/FilterTags';
import {FilterBlockEnum} from '../constants/FilterBlockEnum';
import {FilterFeatures} from './FilterFeatures/FilterFeatures';
import {FilterMakesModels} from './FilterMakesModels/FilterMakesModels';
import {FiltersNavigation} from './FilterNavigation/components/FiltersNavigation';
import {FilterSeller} from './FilterSeller/FilterSeller';
import {FilterSpecification} from './FilterSpecification/FilterSpecification';
import {FilterStatistics} from './FilterStatistics/FilterStatistics';

interface LegacyFiltersProps extends TestIdProps {
  filterPresetsControl: SourcingFilterPresetsControl;
  count: number;
  onClose: () => void;
}

export function LegacyFilters(props: LegacyFiltersProps) {
  const tilesWrapperRef = useRef<HTMLDivElement>(null);
  const {filters} = useFilters();
  const sourcingType = useSourcingType();
  const formatNumber = useFormatNumber();
  const pageHeaderHeight = useSourcingPageHeaderHeight();
  const isCardView = useIsSourcingCardViewEnforced();

  const filterPresetActions: Action[] = [
    {
      type: 'button',
      title: i18n.t('page.filters.actions.showOffersWithCount', {
        count: props.count,
        formattedCount: formatNumber(props.count, 0),
      }),
      onClick: () => {
        props.onClose();
      },
      'data-testid': suffixTestId('showOffersWithCount', props),
    },
  ];

  return (
    <VStack height={`calc(100dvh - ${getSize(pageHeaderHeight)})`}>
      <Box backgroundColor="palettes.white.10.100">
        <Box paddingHorizontal={4} paddingVertical={3}>
          <HStack
            spacing={[2, 2, 4]}
            wrap={isCardView ? true : 'nowrap'}
            justify={isCardView ? 'space-between' : undefined}
          >
            <Hide onMobile onTablet>
              <Center height={8}>
                <Text size="large" alternative>
                  {i18n.t('page.filters.labels.filters')}
                </Text>
              </Center>
              <Show when={!props.filterPresetsControl.isEmpty}>
                <Separator orientation="vertical" spacing={0} />
              </Show>
              <FilterPresetButtons
                filterPresetsControl={props.filterPresetsControl}
                isSortingEnabled
                isCollapsible
              />
            </Hide>
            <Center height={8}>
              <Actions
                actions={filterPresetActions}
                data-testid={suffixTestId('filterLayoutActions', props)}
              />
            </Center>
            <Show onMobile onTablet>
              <SavedFiltersMobileBar
                filterPresetsControl={props.filterPresetsControl}
                isSortingEnabled
              />
            </Show>
          </HStack>
        </Box>
        <Separator spacing={0} />
        <Show
          when={
            hasSomeFilters(
              getApplicableFilters(filters, sourcingType, {
                isVanFiltersEnabled: isFeatureEnabled(featureFlags.SOURCING_VAN_FILTERS),
                isOriginCountryFilterEnabled: isFeatureEnabled(
                  featureFlags.SOURCING_ORIGIN_COUNTRY_FILTER
                ),
                isSuspiciousVehiclesEnabled: isFeatureEnabled(
                  featureFlags.SOURCING_SUSPICIOUS_VEHICLES
                ),
                isSuspiciousReasonFilterEnabled: isSuspiciousReasonFilterEnabled(),
              })
            ) || props.filterPresetsControl.isOutdated
          }
        >
          <Box padding={4}>
            <FilterTags
              filterPresetsControl={props.filterPresetsControl}
              showSaveButton
              data-testid={suffixTestId('filterTags', props)}
            />
          </Box>
          <Separator spacing={0} />
        </Show>
      </Box>
      <Box flex={1} overflow="hidden">
        <HStack height="100%">
          <Hide onMobile onTablet>
            <Box padding={4} paddingRight={0} minWidth={48}>
              <FiltersNavigation
                tilesWrapperRef={tilesWrapperRef}
                data-testid={suffixTestId('filtersNavigation', props)}
              />
            </Box>
          </Hide>
          <Box id="tilesWrapper" flex={1} padding={4} overflowX="hidden" ref={tilesWrapperRef}>
            <VStack spacing={4}>
              <Box id={FilterBlockEnum.seller}>
                <Card title={i18n.t('page.filters.labels.seller')}>
                  <FilterSeller data-testid={suffixTestId('filterSeller', props)} />
                </Card>
              </Box>
              <Box id={FilterBlockEnum.statistics}>
                <Card title={i18n.t('page.filters.labels.statistics')}>
                  <FilterStatistics data-testid={suffixTestId('filterStatistics', props)} />
                </Card>
              </Box>
              <Box id={FilterBlockEnum.makesAndModels}>
                <Card title={i18n.t('page.filters.labels.makeAndModels')}>
                  <FilterMakesModels data-testid={suffixTestId('filterMakesModels', props)} />
                </Card>
              </Box>
              <Box id={FilterBlockEnum.specification}>
                <Card title={i18n.t('page.filters.labels.specification')}>
                  <FilterSpecification data-testid={suffixTestId('filterSpecification', props)} />
                </Card>
              </Box>
              <Box id={FilterBlockEnum.features}>
                <Card title={i18n.t('general.labels.allFeatures')}>
                  <FilterFeatures data-testid={suffixTestId('filterFeatures', props)} />
                </Card>
              </Box>
            </VStack>
          </Box>
        </HStack>
      </Box>
    </VStack>
  );
}
